/* eslint-disable no-console */
import {SyncOutlined} from '@ant-design/icons';
import {Button, Flex, Form, Image, message, Upload} from 'antd';
import type {UploadProps} from 'antd';
import React, {useEffect, useState} from 'react';

import {ReactComponent as TrashIcon} from '../../assets/icons/TrashIcon.svg';
import {ReactComponent as UploadIcon} from '../../assets/icons/UploadIcon.svg';
import {PollingDBStatus} from '../../pages/aiDocumentManager/Constants/commonConstants';
import PdfRender from '../../pages/OCR/PDFRender';
import base64Encode from '../../shared/base64encoder';

const {Dragger} = Upload;

interface Props {
  formInstance: any;
  fieldName?: string;
  height?: number;
  validationMessage?: string;
  allowedExtensions?: string[];
  maxSizeMB?: number;
  typeMessage?: string;
  sizeMessage?: string;
  keepFile?: boolean;
}

const DocumentUploadPreview: React.FC<Props> = ({
  formInstance,
  fieldName = 'location',
  height = 600,
  validationMessage = '',
  allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'],
  maxSizeMB = 2,
  typeMessage = 'Only PDF, JPG, PNG files allowed',
  sizeMessage,
  keepFile = false
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string>('');
  const [fileList, setFileList] = useState<any[]>([]);

  const pollingStatus = Form.useWatch('pollingStatus', formInstance);

  /* ===== Initial Load ===== */
  useEffect(() => {
    const location = formInstance.getFieldValue('location');
    const fileName = formInstance.getFieldValue('file_name');
    if (location) {
      const ext = fileName?.split('.')?.pop()?.toLowerCase();
      if (ext) setFileType(ext);

      if (location instanceof File) {
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(location);
      } else if (!location.startsWith('https')) {
        if (ext === 'pdf') {
          setPreviewUrl(`data:application/pdf;base64,${location}`);
        } else {
          setPreviewUrl(`data:image/${ext};base64,${location}`);
        }
      } else {
        setPreviewUrl(location);
      }
    }
  }, []);

  /* ===== File Validation ===== */
  const beforeUpload = (file: any) => {
    const fileExt = file.name.split('.').pop()?.toLowerCase() ?? '';
    const isValidType = allowedExtensions.includes(fileExt);

    if (!isValidType) {
      void message.error(typeMessage);

      return Upload.LIST_IGNORE;
    }

    const isWithinLimit = file.size / 1024 / 1024 < maxSizeMB;
    if (!isWithinLimit) {
      void message.error(
        sizeMessage ?? `File must be smaller than ${maxSizeMB}MB`
      );

      return Upload.LIST_IGNORE;
    }

    /* ===== Preview ===== */
    const reader = new FileReader();
    reader.onload = async () => {
      setPreviewUrl(reader.result as string);
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      setFileType(ext);

      formInstance.setFieldValue(
        fieldName,
        keepFile ? file : await base64Encode(file as File)
      );
      formInstance.setFieldValue('file_name', file.name);
    };

    reader.readAsDataURL(file);

    return false;
  };

  /* ===== Remove File ===== */
  const handleRemoveFile = () => {
    setFileList([]);
    setPreviewUrl(null);
    setFileType('');
    formInstance.setFieldValue(fieldName, undefined);
    formInstance.setFieldValue('file_name', undefined);
  };

  const uploadProps: UploadProps = {
    accept: allowedExtensions.map((ext) => `.${ext}`).join(','),
    fileList,
    beforeUpload,
    onChange: ({fileList: newFileList}) => {
      setFileList(newFileList);
    }
  };

  const previewContainerStyle = {
    height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    border: '2px dashed grey',
    borderRadius: '10px',
    background: 'rgb(245, 246, 248)'
  };

  return (
    <Form.Item
      name={fieldName}
      rules={[{required: true, message: validationMessage}]}
    >
      {previewUrl && (
        <Flex justify="flex-end" style={{marginBottom: 2}}>
          {pollingStatus === PollingDBStatus.in_progress && (
            <Button
              type="link"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                fontWeight: 500,
                padding: '0px',
                marginRight: '10px'
              }}
            >
              <SyncOutlined spin style={{color: 'grey'}} />
              <span
                style={{
                  color: 'grey',
                  fontWeight: '700',
                  paddingLeft: '4px'
                }}
              >
                Processing ....
              </span>
            </Button>
          )}
          <Button
            type="link"
            danger
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              fontWeight: 500,
              padding: '0px'
            }}
            onClick={handleRemoveFile}
          >
            <TrashIcon />
            <span style={{color: '#DA1E28', fontWeight: '700'}}>Remove</span>
          </Button>
        </Flex>
      )}

      <div style={{minHeight: height, position: 'relative'}}>
        {/* ===== Upload Dragger ===== */}
        {!previewUrl && (
          <Dragger
            {...uploadProps}
            style={{
              minHeight: height,
              width: '100%',
              borderRadius: 12,
              border: '2px dashed #d9d9d9',
              background: '#f5f6f8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <UploadIcon
                style={{
                  fontSize: 48,
                  color: '#8c8c8c',
                  marginBottom: 12
                }}
              />

              <p
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: '#595959',
                  marginBottom: 4
                }}
              >
                Upload or Drag & Drop
              </p>

              <p style={{fontSize: 14, color: '#8c8c8c'}}>
                {allowedExtensions
                  .filter((ext) => ext !== 'jpeg')
                  .map((ext) => `.${ext}`)
                  .join(' | ')}{' '}
                - Max: {maxSizeMB} mb
              </p>
            </div>
          </Dragger>
        )}

        {/* ===== Preview Section ===== */}
        {previewUrl && (
          <>
            {/* ===== Image Preview Centered ===== */}
            {['jpg', 'png', 'jpeg'].includes(fileType) && (
              <div style={previewContainerStyle}>
                <Image
                  src={previewUrl}
                  preview={true}
                  alt="preview"
                  style={{
                    maxWidth: '450px',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}

            {/* ===== PDF Preview Centered ===== */}
            {fileType === 'pdf' && (
              <div style={previewContainerStyle}>
                <div
                  style={{
                    width: '100%',
                    maxHeight: '100%',
                    maxWidth: '450px',
                    height: height - 40
                  }}
                >
                  <PdfRender
                    pdfUrl={previewUrl}
                    handleImageLoad={() => {}}
                    setPage={() => {}}
                    setImgLoaded={() => {}}
                    carouselHeight={height - 40}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Form.Item>
  );
};

export default DocumentUploadPreview;
